import { gql } from '@apollo/client';

// ============================================================================
// CALENDAR QUERIES - Centralized GraphQL operations for the calendars module
// ============================================================================

// ==================== CALENDAR QUERIES ====================

/** CalendarView - basic calendar fields with settings and isActive */
export const GET_CALENDAR = gql`
  query GetCalendar($id: String!) {
    calendar(id: $id) {
      id
      name
      description
      type
      color
      responsibleOwnerId
      settings {
        timezone
        workingHoursStart
        workingHoursEnd
        workingDays
      }
      isActive
    }
  }
`;

/** CalendarDetails - full calendar fields including stats and notifications */
export const GET_CALENDAR_DETAILS = gql`
  query GetCalendar($id: String!) {
    calendar(id: $id) {
      id
      name
      description
      type
      color
      responsibleOwnerId
      visibility
      isPublic
      publicUrl
      linkedEmailAddressId
      settings {
        timezone
        emailNotifications
        smsNotifications
        defaultReminderMinutes
        workingHoursStart
        workingHoursEnd
        workingDays
      }
      totalEvents
      upcomingEvents
      lastEventAt
      isActive
      createdAt
      updatedAt
    }
  }
`;

/** EditCalendar - calendar fields including booking and ownership settings */
export const GET_CALENDAR_EDIT = gql`
  query GetCalendar($id: String!) {
    calendar(id: $id) {
      id
      name
      description
      type
      color
      responsibleOwnerId
      ownerType
      visibility
      isPublic
      linkedEmailAddressId
      companyId
      projectId
      settings {
        timezone
        defaultReminderMinutes
        workingHoursStart
        workingHoursEnd
        workingDays
      }
      isActive
      allowPublicBooking
      bookingPageSlug
      bookingPageTitle
      bookingPageDescription
      bookingPageLogo
      bookingPageProfilePhoto
      requirePaymentUpfront
      sendBookingConfirmations
      sendBookingReminders
    }
  }
`;

export const GET_CALENDARS_BY_IDS = gql`
  query GetCalendarsByIds($ids: [String!]!) {
    getCalendarsByIds(ids: $ids) {
      id
      name
      description
      type
      color
      responsibleOwnerId
      settings {
        timezone
        workingHoursStart
        workingHoursEnd
        workingDays
      }
      isActive
    }
  }
`;

/** CalendarAccountsAdmin - all calendars with full admin fields */
export const GET_ALL_CALENDARS = gql`
  query GetAllCalendars {
    calendars {
      id
      name
      description
      type
      visibility
      responsibleOwnerId
      ownerType
      linkedEmailAddressId
      companyId
      projectId
      color
      isPublic
      totalEvents
      upcomingEvents
      sharedFromEmail
      sharedFromName
      iCalUrl
      allowPublicBooking
      bookingPageSlug
      settings {
        timezone
        emailNotifications
        smsNotifications
        defaultReminderMinutes
      }
      createdAt
      updatedAt
    }
  }
`;

/** BirdsEyeView - basic calendar list */
export const MY_CALENDARS_QUERY = gql`
  query MyCalendars {
    myCalendars {
      id
      name
      type
      description
      responsibleOwnerId
      linkedEmailAddressId
      createdAt
      updatedAt
      lastEventAt
      eventCount
    }
  }
`;

/** MyCalendars - calendar list with booking fields */
export const MY_CALENDARS_WITH_BOOKING_QUERY = gql`
  query MyCalendars {
    myCalendars {
      id
      name
      type
      description
      responsibleOwnerId
      linkedEmailAddressId
      createdAt
      updatedAt
      lastEventAt
      eventCount
      allowPublicBooking
      bookingPageSlug
    }
  }
`;

/** LifeGoalsTimeline - minimal calendar list for finding Life Goals calendar */
export const GET_MY_CALENDARS = gql`
  query GetMyCalendars {
    myCalendars {
      id
      name
      type
    }
  }
`;

// ==================== CALENDAR EVENT QUERIES ====================

/** CalendarView - events with attendees, iCal, and metadata */
export const GET_CALENDAR_EVENTS = gql`
  query GetCalendarEvents($calendarId: String!, $startDate: String, $endDate: String) {
    calendarEvents(calendarId: $calendarId, startDate: $startDate, endDate: $endDate) {
      id
      calendarId
      title
      description
      startTime
      endTime
      isAllDay
      status
      color
      categories
      location {
        name
        address
      }
      attendees {
        clientId
        email
        name
        status
      }
      isCancelled
      isInboundICalInvite
      iCalMethod
      iCalResponseStatus
      iCalOrganizerName
      iCalOrganizerEmail
      metadata
    }
  }
`;

/** CalendarDetails - events with full location, recurrence, reminders */
export const GET_CALENDAR_EVENTS_DETAILED = gql`
  query GetCalendarEvents($calendarId: String!, $startDate: String, $endDate: String) {
    calendarEvents(calendarId: $calendarId, startDate: $startDate, endDate: $endDate) {
      id
      calendarId
      title
      description
      startTime
      endTime
      isAllDay
      status
      color
      location {
        name
        address
        latitude
        longitude
        room
        floor
        instructions
      }
      recurrence {
        frequency
        interval
        count
        until
        byDay
        byMonth
        byMonthDay
        rruleString
      }
      attendees {
        clientId
        email
        name
        status
      }
      reminders {
        method
        minutesBefore
      }
      isCancelled
      cancellationReason
      metadata
    }
  }
`;

export const GET_MULTI_CALENDAR_EVENTS = gql`
  query GetMultiCalendarEvents($calendarIds: [String!]!, $startDate: String, $endDate: String) {
    multiCalendarEvents(calendarIds: $calendarIds, startDate: $startDate, endDate: $endDate) {
      id
      calendarId
      title
      description
      startTime
      endTime
      isAllDay
      status
      color
      categories
      location {
        name
        address
      }
      attendees {
        clientId
        email
        name
        status
      }
      isCancelled
      isInboundICalInvite
      iCalMethod
      iCalResponseStatus
      iCalOrganizerName
      iCalOrganizerEmail
      metadata
    }
  }
`;

/** LifeGoalsTimeline - events with metadata for life goals */
export const GET_LIFE_GOALS = gql`
  query GetLifeGoals($calendarId: String!, $startDate: String!, $endDate: String!) {
    calendarEvents(
      calendarId: $calendarId
      startDate: $startDate
      endDate: $endDate
    ) {
      id
      title
      description
      startTime
      endTime
      metadata
      calendarId
      organizerId
    }
  }
`;

// ==================== EVENT MUTATIONS ====================

export const CREATE_EVENT = gql`
  mutation CreateEvent($input: CalendarEventInput!) {
    createEvent(input: $input) {
      id
      title
      startTime
      endTime
    }
  }
`;

/** CalendarView / EventModal - update event with basic return fields */
export const UPDATE_EVENT = gql`
  mutation UpdateEvent($id: String!, $input: CalendarEventInput!) {
    updateEvent(id: $id, input: $input) {
      id
      title
      startTime
      endTime
    }
  }
`;

/** ICalInviteModal - update event returning categories */
export const UPDATE_EVENT_WITH_CATEGORIES = gql`
  mutation UpdateEvent($id: String!, $input: CalendarEventInput!) {
    updateEvent(id: $id, input: $input) {
      id
      categories
    }
  }
`;

/** BookingDetailsView - update event returning metadata */
export const UPDATE_EVENT_METADATA = gql`
  mutation UpdateEvent($id: String!, $input: CalendarEventInput!) {
    updateEvent(id: $id, input: $input) {
      id
      metadata
    }
  }
`;

/** EventModal - cancel event with optional cancelAll for recurring */
export const CANCEL_EVENT = gql`
  mutation CancelEvent($id: String!, $reason: String, $cancelAll: Boolean) {
    cancelEvent(id: $id, reason: $reason, cancelAll: $cancelAll)
  }
`;

/** CalendarDetails / ICalInviteModal - cancel event without cancelAll */
export const CANCEL_EVENT_SIMPLE = gql`
  mutation CancelEvent($id: String!, $reason: String) {
    cancelEvent(id: $id, reason: $reason)
  }
`;

export const DELETE_EVENT = gql`
  mutation DeleteEvent($id: String!) {
    deleteEvent(id: $id) {
      success
      message
    }
  }
`;

// ==================== LIFE GOAL MUTATIONS ====================

export const CREATE_LIFE_GOAL = gql`
  mutation CreateLifeGoal($input: CalendarEventInput!) {
    createEvent(input: $input) {
      id
      title
      description
      startTime
      endTime
      metadata
      calendarId
    }
  }
`;

export const UPDATE_LIFE_GOAL = gql`
  mutation UpdateLifeGoal($id: String!, $input: CalendarEventInput!) {
    updateEvent(id: $id, input: $input) {
      id
      title
      description
      startTime
      endTime
      metadata
      calendarId
    }
  }
`;

export const DELETE_LIFE_GOAL = gql`
  mutation DeleteLifeGoal($id: String!) {
    deleteEvent(id: $id) {
      success
      message
    }
  }
`;

// ==================== CALENDAR CRUD MUTATIONS ====================

/** NewCalendar - create with basic return fields */
export const CREATE_CALENDAR = gql`
  mutation CreateCalendar($input: BusinessCalendarInput!) {
    createCalendar(input: $input) {
      id
      name
      type
    }
  }
`;

/** CalendarAccountsAdmin - create with more return fields */
export const CREATE_CALENDAR_ADMIN = gql`
  mutation CreateCalendar($input: BusinessCalendarInput!) {
    createCalendar(input: $input) {
      id
      name
      description
      type
      visibility
      color
      responsibleOwnerId
    }
  }
`;

/** EditCalendar - update with basic return */
export const UPDATE_CALENDAR = gql`
  mutation UpdateCalendar($id: String!, $input: BusinessCalendarInput!) {
    updateCalendar(id: $id, input: $input) {
      id
      name
      type
    }
  }
`;

/** CalendarAccountsAdmin - update with acceptedEmailAddresses */
export const UPDATE_CALENDAR_ADMIN = gql`
  mutation UpdateCalendar($id: String!, $input: BusinessCalendarInput!) {
    updateCalendar(id: $id, input: $input) {
      id
      name
      description
      type
      visibility
      color
      acceptedEmailAddresses
    }
  }
`;

export const DELETE_CALENDAR = gql`
  mutation DeleteCalendar($id: String!) {
    deleteCalendar(id: $id)
  }
`;

export const UPLOAD_CALENDAR_PROFILE_PHOTO = gql`
  mutation UploadCalendarProfilePhoto($calendarId: String!, $file: Upload!) {
    uploadCalendarProfilePhoto(calendarId: $calendarId, file: $file) {
      id
      bookingPageProfilePhoto
    }
  }
`;

export const EXPORT_CALENDAR = gql`
  mutation ExportCalendar($calendarId: String!) {
    exportCalendar(calendarId: $calendarId) {
      fileName
      icalData
    }
  }
`;

export const SHARE_CALENDAR = gql`
  mutation ShareCalendar($id: String!, $sharedWithEmail: String!, $permissions: [CalendarPermission!]!) {
    shareCalendar(id: $id, sharedWithEmail: $sharedWithEmail, permissions: $permissions) {
      id
      calendarName
      invitationType
      recipientEmail
      recipientPhone
      shareLink
      notificationSent
      status
      permissions
      expiresAt
    }
  }
`;

// ==================== EXTERNAL CALENDARS ====================

/** CalendarView - external calendars without isActive */
export const GET_EXTERNAL_CALENDARS = gql`
  query GetExternalCalendars {
    getExternalCalendars {
      id
      name
      type
      iCalUrl
      color
      sharedFromName
      sharedFromEmail
      isActive
    }
  }
`;

/** CalendarAccountsAdmin - external calendars with createdAt */
export const GET_EXTERNAL_CALENDARS_ADMIN = gql`
  query GetExternalCalendars {
    getExternalCalendars {
      id
      name
      type
      iCalUrl
      color
      sharedFromName
      sharedFromEmail
      isActive
      createdAt
    }
  }
`;

export const ADD_EXTERNAL_CALENDAR = gql`
  mutation AddExternalCalendar($input: BusinessCalendarInput!) {
    createBusinessCalendar(input: $input) {
      id
      name
      iCalUrl
      color
      type
    }
  }
`;

// ==================== LINKED EMAIL ====================

export const GET_LINKED_EMAIL = gql`
  query GetLinkedEmailForCalendar($calendarId: String!, $tenantId: String) {
    getLinkedEmailForCalendar(calendarId: $calendarId, tenantId: $tenantId)
  }
`;

// ==================== GOALS ====================

export const GET_CURRENT_MONTH_GOALS = gql`
  query GetCurrentMonthGoals($calendarId: String!) {
    currentMonthGoals(calendarId: $calendarId) {
      id
      title
      description
      category
      progressPercentage
      currentValue
      targetValue
      checkpoints {
        id
        title
        completed
      }
      color
      parentGoalId
      assignedTo
    }
  }
`;

export const GET_CALENDAR_GOALS = gql`
  query GetCalendarGoals($calendarId: String!, $period: String) {
    calendarGoals(calendarId: $calendarId, period: $period) {
      id
      title
      description
      category
      period
      status
      startDate
      endDate
      progressPercentage
      currentValue
      targetValue
      currentNumericValue
      targetNumericValue
      checkpoints {
        id
        title
        description
        completed
        completedAt
        dueDate
        order
        weight
        notes
      }
      color
      icon
      displayOrder
      kpiMetric
      kpiUnit
      notes
      tags
      parentGoalId
      assignedTo
    }
  }
`;

export const CREATE_GOAL = gql`
  mutation CreateCalendarGoal($input: CalendarGoalInput!) {
    createCalendarGoal(input: $input) {
      id
      title
      progressPercentage
    }
  }
`;

export const UPDATE_GOAL = gql`
  mutation UpdateCalendarGoal($id: String!, $input: CalendarGoalInput!) {
    updateCalendarGoal(id: $id, input: $input) {
      id
      title
      progressPercentage
    }
  }
`;

export const DELETE_GOAL = gql`
  mutation DeleteCalendarGoal($id: String!) {
    deleteCalendarGoal(id: $id)
  }
`;

export const UPDATE_CHECKPOINT = gql`
  mutation UpdateGoalCheckpoint($input: UpdateGoalCheckpointInput!) {
    updateGoalCheckpoint(input: $input) {
      id
      progressPercentage
      checkpoints {
        id
        completed
      }
    }
  }
`;

// ==================== TAGS ====================

export const GET_CALENDAR_TAGS = gql`
  query GetCalendarTags($calendarId: String!) {
    calendarTags(calendarId: $calendarId) {
      id
      name
      color
      description
      usageCount
    }
  }
`;

/** TagManager - upsert tag with usageCount */
export const UPSERT_CALENDAR_TAG = gql`
  mutation UpsertCalendarTag($calendarId: String!, $input: CalendarTagInput!) {
    upsertCalendarTag(calendarId: $calendarId, input: $input) {
      id
      name
      color
      description
      usageCount
    }
  }
`;

/** TagSelector - upsert tag without usageCount */
export const UPSERT_CALENDAR_TAG_BASIC = gql`
  mutation UpsertCalendarTag($calendarId: String!, $input: CalendarTagInput!) {
    upsertCalendarTag(calendarId: $calendarId, input: $input) {
      id
      name
      color
      description
    }
  }
`;

export const DELETE_CALENDAR_TAG = gql`
  mutation DeleteCalendarTag($calendarId: String!, $name: String!) {
    deleteCalendarTag(calendarId: $calendarId, name: $name)
  }
`;

export const SUGGEST_TAG_COLORS = gql`
  query SuggestTagColors {
    suggestTagColors
  }
`;

// ==================== CLIENTS ====================

/** EventModal - clients with tags */
export const GET_CLIENTS_WITH_TAGS = gql`
  query GetClientsWithTags {
    searchClients(search: "") {
      id
      fName
      lName
      email
      phoneNumber
      tags
    }
  }
`;

/** GoalsModal - clients with businessName */
export const GET_CLIENTS = gql`
  query GetClients {
    clients {
      id
      fName
      lName
      email
      businessName
    }
  }
`;

/** CalendarView - clients for goals (fName, lName, businessName) */
export const GET_CLIENTS_FOR_GOALS = gql`
  query GetClients {
    clients {
      id
      fName
      lName
      businessName
    }
  }
`;

/** CalendarAccountsAdmin - clients with phone */
export const GET_CLIENTS_ADMIN = gql`
  query GetClients {
    clients {
      id
      fName
      lName
      email
      phoneNumber
    }
  }
`;

// ==================== USER / AUTH ====================

/** EventModal - get current user's tenant */
export const GET_ME = gql`
  query GetMe {
    me {
      id
      tenantId
    }
  }
`;

/** MyCalendars - get current user details */
export const ME_QUERY = gql`
  query Me {
    me {
      id
      fName
      lName
      email
    }
  }
`;

export const GET_TENANT_SMS_CONFIG = gql`
  query GetTenantSmsConfig($id: ID!) {
    tenant(id: $id) {
      id
      alphaId
      dedicatedNumber
      useAlphaId
    }
  }
`;

// ==================== BOOKABLE EVENT TYPES ====================

export const BOOKABLE_EVENT_TYPES_QUERY = gql`
  query BookableEventTypes($calendarId: String!) {
    bookableEventTypes(calendarId: $calendarId) {
      id
      calendarId
      name
      description
      color
      icon
      durationMinutes
      isPaid
      price
      currency
      stripePriceId
      isActive
      maxBookingsPerDay
      minNoticeHours
      maxFutureDays
      bufferBeforeMinutes
      bufferAfterMinutes
      locationType
      location
      meetingLink
      autoGenerateMeetingLink
      questions {
        id
        question
        required
        type
        options
      }
      confirmationMessage
      reminderHoursBefore
      displayOrder
      totalBookings
      lastBookedAt
      createdAt
    }
  }
`;

export const CREATE_BOOKABLE_EVENT_TYPE = gql`
  mutation CreateBookableEventType($input: CreateBusinessCalendarBookableEventTypeInput!) {
    createBookableEventType(input: $input) {
      id
      name
      durationMinutes
      isPaid
      price
      isActive
    }
  }
`;

export const UPDATE_BOOKABLE_EVENT_TYPE = gql`
  mutation UpdateBookableEventType($id: String!, $input: UpdateBusinessCalendarBookableEventTypeInput!) {
    updateBookableEventType(id: $id, input: $input) {
      id
      name
      durationMinutes
      isPaid
      price
      isActive
    }
  }
`;

export const DELETE_BOOKABLE_EVENT_TYPE = gql`
  mutation DeleteBookableEventType($id: String!) {
    deleteBookableEventType(id: $id)
  }
`;

export const TOGGLE_EVENT_TYPE_STATUS = gql`
  mutation ToggleBookableEventTypeStatus($id: String!) {
    toggleBookableEventTypeStatus(id: $id) {
      id
      isActive
    }
  }
`;

// ==================== AVAILABILITY ====================

export const GET_CALENDAR_AVAILABILITY = gql`
  query CalendarAvailability($calendarId: String!) {
    calendarAvailability(calendarId: $calendarId) {
      id
      calendarId
      slots {
        dayOfWeek
        startTime
        endTime
      }
      blockedDates
      extraAvailableDates
      timezone
    }
  }
`;

export const CREATE_AVAILABILITY = gql`
  mutation CreateBusinessCalendarAvailability($input: CreateBusinessCalendarAvailabilityInput!) {
    createBusinessCalendarAvailability(input: $input) {
      id
      calendarId
      slots {
        dayOfWeek
        startTime
        endTime
      }
      blockedDates
      timezone
    }
  }
`;

export const UPDATE_AVAILABILITY = gql`
  mutation UpdateBusinessCalendarAvailability($calendarId: String!, $input: UpdateBusinessCalendarAvailabilityInput!) {
    updateBusinessCalendarAvailability(calendarId: $calendarId, input: $input) {
      id
      calendarId
      slots {
        dayOfWeek
        startTime
        endTime
      }
      blockedDates
      timezone
    }
  }
`;

// ==================== PUBLIC BOOKING ====================

/** PublicBookingPage - calendar with full booking page fields */
export const PUBLIC_CALENDAR_QUERY = gql`
  query PublicCalendar($slug: String!) {
    publicCalendar(slug: $slug) {
      id
      name
      description
      color
      bookingPageSlug
      bookingPageTitle
      bookingPageDescription
      bookingPageLogo
      bookingPageProfilePhoto
    }
  }
`;

/** PublicBookingEventPage - calendar with basic booking fields */
export const PUBLIC_CALENDAR_BASIC_QUERY = gql`
  query PublicCalendar($slug: String!) {
    publicCalendar(slug: $slug) {
      id
      name
      description
      color
      bookingPageSlug
      bookingPageTitle
      bookingPageDescription
    }
  }
`;

/** PublicBookingConfirmPage - calendar with minimal booking fields */
export const PUBLIC_CALENDAR_CONFIRM_QUERY = gql`
  query PublicCalendar($slug: String!) {
    publicCalendar(slug: $slug) {
      id
      name
      bookingPageTitle
      bookingPageProfilePhoto
      bookingPageLogo
    }
  }
`;

export const PUBLIC_EVENT_TYPES_QUERY = gql`
  query PublicBookableEventTypes($calendarSlug: String!) {
    publicBookableEventTypes(calendarSlug: $calendarSlug) {
      id
      name
      description
      durationMinutes
      isPaid
      price
      currency
      locationType
      location
      meetingLink
      isActive
    }
  }
`;

/** PublicBookingEventPage - same fields, different operation name */
export const PUBLIC_EVENT_TYPE_QUERY = gql`
  query PublicEventType($calendarSlug: String!) {
    publicBookableEventTypes(calendarSlug: $calendarSlug) {
      id
      name
      description
      durationMinutes
      isPaid
      price
      currency
      locationType
      location
      meetingLink
      isActive
    }
  }
`;

export const AVAILABLE_TIME_SLOTS_QUERY = gql`
  query AvailableTimeSlots(
    $calendarId: String!
    $eventTypeId: String!
    $startDate: String!
    $endDate: String!
  ) {
    availableTimeSlots(
      calendarId: $calendarId
      eventTypeId: $eventTypeId
      startDate: $startDate
      endDate: $endDate
    ) {
      startTime
      endTime
      available
    }
  }
`;

export const CREATE_PUBLIC_BOOKING = gql`
  mutation CreatePublicBooking($input: CreatePublicBookingInput!) {
    createPublicBooking(input: $input) {
      booking {
        id
        title
        startTime
        endTime
      }
      bookingToken
      requiresPayment
      paymentAmount
      paymentCurrency
      stripeCheckoutUrl
    }
  }
`;

// ==================== MANAGE BOOKING ====================

export const GET_BOOKING_BY_TOKEN = gql`
  query GetBookingByToken($token: String!) {
    getBookingByToken(token: $token) {
      id
      title
      startTime
      endTime
      metadata
    }
  }
`;

export const CANCEL_BOOKING = gql`
  mutation CancelBooking($token: String!, $reason: String!) {
    cancelBooking(token: $token, reason: $reason)
  }
`;

// ==================== ICAL / RSVP ====================

export const SEND_RSVP_RESPONSE = gql`
  mutation SendRSVPResponse($eventId: String!, $response: String!, $fromEmail: String) {
    sendRSVPResponse(eventId: $eventId, response: $response, fromEmail: $fromEmail) {
      success
      message
      event {
        id
        iCalResponseStatus
      }
    }
  }
`;

export const EMAIL_SENDERS_QUERY = gql`
  query GetEmailSenders {
    emailSenders
  }
`;

export const USER_EMAIL_ACCOUNTS_QUERY = gql`
  query GetUserEmailAccounts {
    userEmailAccounts {
      id
      email
      provider
      isDefault
      isActive
    }
  }
`;

// ==================== CALENDAR ACCOUNTS ADMIN ====================

export const GET_VERIFIED_EMAIL_ADDRESSES = gql`
  query GetVerifiedEmailAddresses {
    emailAddresses(isVerified: true) {
      id
      email
      name
      type
      isVerified
      isRegisteredWithImprovMX
      emailsReceived
      emailsSent
      domain
    }
  }
`;

export const ADD_ACCEPTED_EMAIL = gql`
  mutation AddAcceptedEmailToCalendar($calendarId: String!, $email: String!) {
    addAcceptedEmailToCalendar(calendarId: $calendarId, email: $email) {
      id
      acceptedEmailAddresses
    }
  }
`;

// Calendar sharing mutations removed - sharing is now managed via Email module (EmailAddress.linkedCalendars)

export const REMOVE_ACCEPTED_EMAIL = gql`
  mutation RemoveAcceptedEmailFromCalendar($calendarId: String!, $email: String!) {
    removeAcceptedEmailFromCalendar(calendarId: $calendarId, email: $email) {
      id
      acceptedEmailAddresses
    }
  }
`;

export const TRANSFER_OWNERSHIP = gql`
  mutation TransferCalendarOwnership($calendarId: String!, $newOwnerId: String) {
    transferCalendarOwnership(calendarId: $calendarId, newOwnerId: $newOwnerId) {
      id
      responsibleOwnerId
    }
  }
`;

// ==================== COMPANIES / EMPLOYEES ====================

export const GET_COMPANIES = gql`
  query GetCompanies {
    companies {
      id
      name
    }
  }
`;

export const GET_EMPLOYEES = gql`
  query GetEmployees {
    employees {
      id
      fName
      lName
      email
    }
  }
`;

export const GET_ASSIGNED_EMAIL_ADDRESSES = gql`
  query GetAssignedEmailAddresses {
    emailAddresses {
      email
      name
      type
      associatedClients
    }
  }
`;

// ==================== BOOKING DETAILS VIEW ====================

export const GET_CLIENT_BY_EMAIL = gql`
  query GetClientByEmail($email: String!) {
    clientByEmail(email: $email) {
      id
      fName
      lName
      email
      phoneNumber
    }
  }
`;

export const CREATE_PROJECT = gql`
  mutation CreateProject($input: ProjectInput!) {
    createProject(input: $input) {
      id
      projectName
      projectGoal
      projectDescription
      billingClient {
        id
        fName
        lName
      }
    }
  }
`;

export const GET_PROJECT_BY_ID = gql`
  query GetProject($id: ID!) {
    project(id: $id) {
      id
      projectName
    }
  }
`;

// ==================== OPPORTUNITIES ====================

export const GET_OPPORTUNITIES = gql`
  query GetOpportunities($status: OpportunityStatus, $stage: String) {
    opportunities(status: $status, stage: $stage) {
      id
      title
      description
      clientName
      value
      stage
      status
      priority
      probability
      expectedRevenue
      expectedCloseDate
      assignedToName
    }
  }
`;

// ==================== EVENT TASKS ====================

export const GET_EVENT_TASKS = gql`
  query GetEventTasks($calendarEventId: String!) {
    calendarEventTasks(calendarEventId: $calendarEventId) {
      id
      title
      description
      status
      completed
      completedAt
      completedBy
      dueDate
      order
      assignedTo
      notes
      totalChecklistItems
      completedChecklistItems
      progressPercentage
      checklistItems {
        id
        title
        completed
        completedAt
        completedBy
        order
      }
      createdAt
      updatedAt
    }
  }
`;

export const CREATE_EVENT_TASK = gql`
  mutation CreateEventTask($input: CalendarEventTaskInput!) {
    createCalendarEventTask(input: $input) {
      id
      title
      description
      status
      completed
      completedAt
      dueDate
      order
      assignedTo
      notes
      totalChecklistItems
      completedChecklistItems
      progressPercentage
      checklistItems {
        id
        title
        completed
        completedAt
        order
      }
    }
  }
`;

export const UPDATE_EVENT_TASK = gql`
  mutation UpdateEventTask($id: String!, $input: CalendarEventTaskInput!) {
    updateCalendarEventTask(id: $id, input: $input) {
      id
      title
      description
      status
      completed
      completedAt
      dueDate
      order
      assignedTo
      notes
      totalChecklistItems
      completedChecklistItems
      progressPercentage
      checklistItems {
        id
        title
        completed
        completedAt
        order
      }
    }
  }
`;

export const TOGGLE_TASK_COMPLETION = gql`
  mutation ToggleTaskCompletion($id: String!) {
    toggleCalendarEventTaskCompletion(id: $id) {
      id
      completed
      completedAt
      status
      progressPercentage
    }
  }
`;

export const DELETE_EVENT_TASK = gql`
  mutation DeleteEventTask($id: String!) {
    deleteCalendarEventTask(id: $id)
  }
`;

export const ADD_CHECKLIST_ITEM = gql`
  mutation AddChecklistItem($taskId: String!, $item: CalendarEventTaskChecklistItemInput!) {
    addCalendarEventTaskChecklistItem(taskId: $taskId, item: $item) {
      id
      checklistItems {
        id
        title
        completed
        completedAt
        order
      }
      totalChecklistItems
      completedChecklistItems
      progressPercentage
    }
  }
`;

export const UPDATE_CHECKLIST_ITEM = gql`
  mutation UpdateChecklistItem($input: UpdateCalendarEventTaskChecklistInput!) {
    updateCalendarEventTaskChecklistItem(input: $input) {
      id
      checklistItems {
        id
        title
        completed
        completedAt
        order
      }
      totalChecklistItems
      completedChecklistItems
      progressPercentage
      completed
      status
    }
  }
`;

export const REMOVE_CHECKLIST_ITEM = gql`
  mutation RemoveChecklistItem($taskId: String!, $checklistItemId: String!) {
    removeCalendarEventTaskChecklistItem(taskId: $taskId, checklistItemId: $checklistItemId) {
      id
      checklistItems {
        id
        title
        completed
        order
      }
      totalChecklistItems
      completedChecklistItems
      progressPercentage
    }
  }
`;

export const GET_INCOMPLETE_TASKS = gql`
  query GetIncompleteTasks($calendarId: String) {
    incompleteTasks(calendarId: $calendarId) {
      id
      title
      description
      status
      completed
      dueDate
      calendarEventId
      totalChecklistItems
      completedChecklistItems
      progressPercentage
      checklistItems {
        id
        title
        completed
        order
      }
    }
  }
`;

// ==================== TEST QUERIES ====================
// These are used by test files and have different field shapes

export const TEST_GET_CALENDAR = gql`
  query GetCalendar($id: ID!) {
    calendar(id: $id) {
      id
      name
      type
      description
      color
      visibility
      settings {
        workingHoursStart
        workingHoursEnd
        timezone
      }
    }
  }
`;

export const TEST_GET_EVENTS = gql`
  query GetEvents($calendarId: ID!) {
    events(calendarId: $calendarId) {
      id
      title
      start
      end
      allDay
      color
      description
      location
      attendees
      status
    }
  }
`;

export const TEST_GET_EVENTS_BASIC = gql`
  query GetEvents($calendarId: ID!) {
    events(calendarId: $calendarId) {
      id
      title
      start
      end
      allDay
      color
      description
    }
  }
`;

export const TEST_CREATE_EVENT = gql`
  mutation CreateEvent($input: EventInput!) {
    createEvent(input: $input) {
      id
      title
      start
      end
      allDay
      color
      description
    }
  }
`;

export const TEST_UPDATE_EVENT = gql`
  mutation UpdateEvent($id: ID!, $input: EventInput!) {
    updateEvent(id: $id, input: $input) {
      id
      title
      start
      end
      allDay
      color
      description
    }
  }
`;

export const TEST_DELETE_EVENT = gql`
  mutation DeleteEvent($id: ID!) {
    deleteEvent(id: $id)
  }
`;

/** PublicBookingPage.test - public calendar query without profilePhoto */
export const TEST_PUBLIC_CALENDAR_QUERY = gql`
  query PublicCalendar($slug: String!) {
    publicCalendar(slug: $slug) {
      id
      name
      description
      color
      bookingPageSlug
      bookingPageTitle
      bookingPageDescription
      bookingPageLogo
    }
  }
`;

/** PublicBookingPage.test - same as PUBLIC_EVENT_TYPES_QUERY */
export const TEST_PUBLIC_EVENT_TYPES_QUERY = gql`
  query PublicBookableEventTypes($calendarSlug: String!) {
    publicBookableEventTypes(calendarSlug: $calendarSlug) {
      id
      name
      description
      durationMinutes
      isPaid
      price
      currency
      locationType
      location
      meetingLink
      isActive
    }
  }
`;
